import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../../utils/api";
import { toast } from "../ui/sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ListTodo,
  Loader2,
  Plus,
  Star,
  X,
} from "lucide-react";
import { accentBg, accentText } from "../../utils/accent";
import QuickCanvasModal from "./QuickCanvasModal";
import RailHeader from "./RailHeader";
import TaskRow from "./TaskRow";

const hoje = () => new Date().toISOString().slice(0, 10);

const FILTROS = [
  ["todas", "Todas", ListTodo],
  ["hoje", "Hoje", CalendarDays],
  ["atrasadas", "Atrasadas", AlertTriangle],
  ["proximas", "Próximas", CalendarClock],
  ["importantes", "Importantes", Star],
  ["feitas", "Concluídas", CheckCircle2],
];

const combina = (t, filtro) => {
  const h = hoje();
  if (filtro === "feitas") return t.done;
  if (t.done) return false;
  if (filtro === "todas") return true;
  if (filtro === "hoje") return t.due_date === h;
  if (filtro === "atrasadas") return !!t.due_date && t.due_date < h;
  if (filtro === "importantes") return !!t.starred;
  return !t.due_date || t.due_date > h;
};

/** Em "Todas": sem data primeiro, depois por data/hora crescente. */
const porData = (a, b) => {
  if (!a.due_date && !b.due_date) return 0;
  if (!a.due_date) return -1;
  if (!b.due_date) return 1;
  return `${a.due_date} ${a.due_time || ""}`.localeCompare(`${b.due_date} ${b.due_time || ""}`);
};

/** Tarefas em modal canvas com edição inline no estilo Google Tarefas. */
export default function QuickTasksModal({ open, onClose }) {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState("todas");
  const [novoTitulo, setNovoTitulo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [confirmaExclusao, setConfirmaExclusao] = useState(null);
  const [arrastando, setArrastando] = useState(null);
  const composerRef = useRef(null);
  const ordemMudou = useRef(false);

  const buscar = async () => {
    try {
      const { data } = await api.get("/tasks", { params: { include_done: true } });
      setItens(Array.isArray(data) ? data : []);
    } catch {
      setItens([]);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setCarregando(true);
    setEditando(null);
    buscar();
    setTimeout(() => composerRef.current?.focus(), 120);
  }, [open]);

  const criar = async () => {
    const titulo = novoTitulo.trim();
    if (!titulo) return;
    setSalvando(true);
    try {
      const payload = { title: titulo };
      if (filtro === "hoje") payload.due_date = hoje();
      if (filtro === "importantes") payload.starred = true;
      const { data } = await api.post("/tasks", payload);
      setItens((l) => [data, ...l]);
      setNovoTitulo("");
      composerRef.current?.focus();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível criar a tarefa."));
    } finally {
      setSalvando(false);
    }
  };

  const atualizar = async (id, campos) => {
    setItens((l) => l.map((t) => (t.id === id ? { ...t, ...campos } : t)));
    try {
      const { data } = await api.patch(`/tasks/${id}`, campos);
      setItens((l) => l.map((t) => (t.id === id ? data : t)));
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar."));
      buscar();
    }
  };

  const alternar = async (t) => {
    await atualizar(t.id, { done: !t.done });
    if (!t.done) toast.success("Tarefa concluída");
  };

  const excluir = async () => {
    const alvo = confirmaExclusao;
    setConfirmaExclusao(null);
    setEditando(null);
    setItens((l) => l.filter((t) => t.id !== alvo.id && t.parent_id !== alvo.id));
    try {
      await api.delete(`/tasks/${alvo.id}`);
      toast.success("Tarefa excluída.");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível excluir."));
      buscar();
    }
  };

  const persistirOrdem = async (lista) => {
    try {
      await api.post("/tasks/reorder", {
        items: lista.map((t) => ({ id: t.id, parent_id: t.parent_id || null })),
      });
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar a ordem."));
      buscar();
    }
  };

  const arrasto = {
    arrastando,
    iniciar: (id) => setArrastando(id),
    sobre: (id) => {
      if (!arrastando || arrastando === id) return;
      setItens((l) => {
        const a = l.findIndex((t) => t.id === arrastando);
        const b = l.findIndex((t) => t.id === id);
        if (a < 0 || b < 0 || (l[a].parent_id || null) !== (l[b].parent_id || null)) return l;
        const copia = [...l];
        const [item] = copia.splice(a, 1);
        copia.splice(b, 0, item);
        ordemMudou.current = true;
        return copia;
      });
    },
    terminar: () => {
      setArrastando(null);
      if (ordemMudou.current) {
        ordemMudou.current = false;
        persistirOrdem(itens);
      }
    },
  };

  const indentar = async (t) => {
    const idx = itens.findIndex((x) => x.id === t.id);
    const pai = [...itens.slice(0, idx)].reverse().find((x) => !x.parent_id);
    if (!pai) return;
    if (itens.some((x) => x.parent_id === t.id))
      return toast.error("Promova as subtarefas antes de indentar esta tarefa.");
    // coloca logo depois do último filho do pai: assim a linha só anda para a direita
    const resto = itens.filter((x) => x.id !== t.id);
    const iPai = resto.findIndex((x) => x.id === pai.id);
    let destino = iPai + 1;
    while (destino < resto.length && resto[destino].parent_id === pai.id) destino += 1;
    const nova = [...resto];
    nova.splice(destino, 0, { ...t, parent_id: pai.id });
    setItens(nova);
    try {
      await api.patch(`/tasks/${t.id}`, { parent_id: pai.id });
      await persistirOrdem(nova);
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível criar a subtarefa."));
      buscar();
    }
  };

  const promover = async (t) => {
    const nova = itens.map((x) => (x.id === t.id ? { ...x, parent_id: null } : x));
    setItens(nova);
    try {
      await api.patch(`/tasks/${t.id}`, { parent_id: null });
      await persistirOrdem(nova);
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível promover a tarefa."));
      buscar();
    }
  };

  const contagens = useMemo(() => {
    const acc = {};
    FILTROS.forEach(([id]) => {
      acc[id] = itens.filter((t) => combina(t, id)).length;
    });
    return acc;
  }, [itens]);

  const visiveis = useMemo(() => {
    let raizes = itens.filter((t) => !t.parent_id && combina(t, filtro));
    if (filtro === "todas") raizes = [...raizes].sort(porData);
    return raizes.map((t) => ({
      pai: t,
      filhas: itens.filter((f) => f.parent_id === t.id && (filtro === "feitas" ? f.done : !f.done)),
    }));
  }, [itens, filtro]);

  const rotuloFiltro = FILTROS.find((f) => f[0] === filtro)?.[1];

  const rail = (
    <>
      <RailHeader label="Minhas tarefas" />
      <nav className="p-3 pt-1 flex-1 min-h-0 overflow-y-auto space-y-1">
        {FILTROS.map(([id, rotulo, Icone]) => {
          const ativo = filtro === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => {
                setFiltro(id);
                setEditando(null);
              }}
              className={`flex items-center gap-2 w-full px-3 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap border ${
                ativo
                  ? "bg-white text-gray-900 border-gray-200 shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 border-transparent"
              }`}
              data-testid={`quick-task-filter-${id}`}
            >
              <Icone
                size={14}
                className={ativo ? "" : "text-gray-400"}
                style={ativo ? accentText() : undefined}
              />
              <span className="flex-1 text-left">{rotulo}</span>
              {(contagens[id] || 0) > 0 && (
                <span className="text-[10px] leading-none bg-gray-100 text-gray-500 rounded-full px-1.5 py-0 font-bold inline-flex items-center justify-center min-w-[16px] h-4">
                  {contagens[id]}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="px-4 py-3 border-t border-gray-200">
        <p className="text-[11px] text-gray-400 leading-relaxed">
          Clique numa tarefa para editar ali mesmo — tudo salva sozinho.{" "}
          <kbd className="px-1 py-0.5 rounded bg-white border border-gray-200 text-gray-500 text-[10px]">
            Tab
          </kbd>{" "}
          vira subtarefa.
        </p>
      </div>
    </>
  );

  return (
    <>
      <QuickCanvasModal
        open={open}
        onClose={onClose}
        title="Tarefas"
        subtitle="Sua lista pessoal, só sua"
        icon={ListTodo}
        rail={rail}
        testId="quick-tasks-modal"
        onEscapeKeyDown={(e) => {
          if (editando) {
            e.preventDefault();
            setEditando(null);
          }
        }}
      >
        <div className="px-6 pt-5 pb-4 shrink-0 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Input
              ref={composerRef}
              value={novoTitulo}
              onChange={(e) => setNovoTitulo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") criar();
              }}
              placeholder="O que precisa ser feito?"
              className="rounded-lg h-9"
              data-testid="quick-task-new-input"
            />
            <Button
              type="button"
              onClick={criar}
              disabled={salvando || !novoTitulo.trim()}
              className="h-9 text-white gap-1.5 shrink-0"
              style={accentBg()}
              data-testid="quick-task-new-save"
            >
              {salvando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus size={15} />
              )}
              Adicionar
            </Button>
          </div>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-4"
          onMouseDown={(e) => {
            if (editando && !e.target.closest("[data-testid^='quick-task-']")) setEditando(null);
          }}
        >
          {carregando ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
            </div>
          ) : !visiveis.length ? (
            <div className="text-center py-16 text-gray-400" data-testid="quick-task-empty">
              <ListTodo className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-semibold">Nenhuma tarefa em “{rotuloFiltro}”</p>
              <p className="text-xs mt-1">
                As tarefas que você criar aqui aparecem nesta lista e avisam você na hora marcada.
              </p>
            </div>
          ) : (
            <div>
              {visiveis.map(({ pai, filhas }) => {
                const idx = itens.findIndex((x) => x.id === pai.id);
                const podeIndentar = itens.slice(0, idx).some((x) => !x.parent_id);
                return (
                  <div key={pai.id}>
                    <TaskRow
                      t={pai}
                      aberto={editando === pai.id}
                      atrasada={!pai.done && !!pai.due_date && pai.due_date < hoje()}
                      onAbrir={setEditando}
                      onFechar={() => setEditando(null)}
                      onToggle={alternar}
                      onPatch={(campos) => atualizar(pai.id, campos)}
                      onExcluir={setConfirmaExclusao}
                      podeIndentar={podeIndentar}
                      onIndent={() => indentar(pai)}
                      onOutdent={() => promover(pai)}
                      arrasto={arrasto}
                    />
                    {filhas.map((f) => (
                      <TaskRow
                        key={f.id}
                        t={f}
                        filha
                        aberto={editando === f.id}
                        atrasada={!f.done && !!f.due_date && f.due_date < hoje()}
                        onAbrir={setEditando}
                        onFechar={() => setEditando(null)}
                        onToggle={alternar}
                        onPatch={(campos) => atualizar(f.id, campos)}
                        onExcluir={setConfirmaExclusao}
                        podeIndentar={false}
                        onIndent={() => {}}
                        onOutdent={() => promover(f)}
                        arrasto={arrasto}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 shrink-0 bg-white">
          <p className="text-xs text-gray-500 flex-1 min-w-0 truncate">
            {rotuloFiltro} · {contagens[filtro] || 0} tarefa(s)
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="h-9 gap-1.5"
            data-testid="quick-task-close"
          >
            <X size={15} /> Fechar
          </Button>
        </div>
      </QuickCanvasModal>

      <AlertDialog open={!!confirmaExclusao} onOpenChange={(v) => !v && setConfirmaExclusao(null)}>
        <AlertDialogContent
          className="z-[1800]"
          overlayClassName="z-[1799]"
          data-testid="quick-task-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Excluir tarefa?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A tarefa <strong>{confirmaExclusao?.title}</strong> será excluída
              {confirmaExclusao && itens.some((t) => t.parent_id === confirmaExclusao.id)
                ? ", junto com suas subtarefas"
                : ""}
              . Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={excluir}
              data-testid="quick-task-delete-confirm"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
